/**
 * CalendarV2Page - Nova versió del calendari amb dades reals
 * Mobile-first, modular, SVG-based
 *
 * URLs: `/calendar-v2` i `/reserves/:ids` (F1 — la reserva té adreça).
 *
 * Amb `:ids` la pàgina és la mateixa: el calendari se situa a la setmana del check-in, a la
 * propietat que toca, i la `ReservationSheet` s'obre amb aquella reserva. **L'URL és l'estat**:
 * no hi ha cap `useState` de "reserva seleccionada", i per això una recàrrega o un enllaç
 * enviat per WhatsApp obren exactament el mateix.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, RotateCw } from 'lucide-react';
import { CalendarToolbar } from './CalendarToolbar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  CalendarGrid,
  CalendarThemeProvider,
  CreateReservationSheet,
  useCalendarTheme,
  type CreateReservationTarget,
  type ReservationCellData,
  type RoomInfo,
} from '@/components/calendar-v2';
import type { CalendarGridSection } from '@/components/calendar-v2/CalendarGrid';
import { CalendarLegend } from '@/components/calendar-v2/CalendarLegend';
import type { CreatedReservation } from '@/hooks/useCreateReservation';
import { useAuth } from '@/contexts/AuthContext';
import {
  CalendarSettingsDialog,
  useCalendarSettings,
  type RoomSortMode,
} from '@/components/calendar-v2/CalendarSettingsDialog';
import { calendarBookingsQueryOptions } from '@/hooks/useCalendarBookings';
import { calendarUnitsQueryOptions } from '@/hooks/useCalendarUnits';
import { useProperties, type Property } from '@/hooks/useProperties';
import type { Booking, Unit } from '@/services/api';
import { compareUnitsBySortOrder } from './lib/sort-units';
import { bookingsToReservations, countBedsToReview, isLiveBedToReview } from './lib/transform-booking';
import {
  readPropertyView,
  readSavedPropertyId,
  resolveSelectedPropertyId,
  savePropertyView,
  saveSelectedPropertyId,
  calendarBlockState,
  sortPropertiesForCalendar,
  stableDateRange,
  type PropertyView,
} from './property-view';
import { api } from '@/services/api';
import { ReservationSheet } from '@/components/reservation-sheet';
import { StayCatchUp } from '@/components/stay-catch-up';
import { cn } from '@/lib/utils';
import {
  buildGroupPathFocusedOn,
  buildReservationPath,
  CALENDAR_PATH,
  parseReservationIds,
  startOfWeekMonday,
} from '@/lib/reservation-url';

// ============================================
// HELPERS
// ============================================

function generateDates(startDate: Date, days: number): Date[] {
  const dates: Date[] = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }
  return dates;
}

/**
 * Resposta de `GET /reservations/by-beds24/:beds24Id` (el mateix DTO que `/reservations/:id`).
 * Només els camps que necessita la cel·la mínima; la fitxa carrega la resta per id.
 */
interface ReservationDetailDto {
  id: string;
  beds24_id?: string;
  beds24_property_id?: number;
  /** Número de la reserva mestra del grup de Beds24. La mestra el porta buit. */
  master_id?: string;
  booking_group?: string;
  /** Totes les reserves del grup de Beds24, aquesta inclosa. Només si n'hi ha més d'una. */
  group_beds24_ids?: string[];
  guest_name?: string;
  guest_phone?: string;
  guest_country?: string;
  guest_country2?: string;
  canal?: string;
  estat?: string;
  data_entrada: string;
  data_sortida: string;
  nits?: number;
  num_adults?: number;
  num_nens?: number;
  total_hostes?: number;
  hora_arribada?: string;
  preu_total?: number;
  deposit?: number;
  balance?: number;
  unit_id?: string;
  room_id?: string;
  guests?: unknown[];
}

const DTO_STATUS_MAP: Record<string, ReservationCellData['status']> = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
  CANCELLED: 'cancelled',
  // Senyals de la card (§6 F1.10): com a `transform-booking.ts`; abans queia a 'confirmed'.
  NO_SHOW: 'no_show',
};

/**
 * DTO de la reserva → `ReservationCellData` mínima.
 *
 * Fa falta quan la reserva de l'URL no és a la graella carregada (fora del rang de dates o
 * d'una altra propietat): la fitxa necessita una cel·la per obrir-se, i ella mateixa torna a
 * demanar el detall complet per `id`. No s'hi posa res que no vingui del DTO.
 */
function reservationDtoToCellData(dto: ReservationDetailDto): ReservationCellData {
  return {
    id: dto.id,
    beds24Id: dto.beds24_id,
    guestName: dto.guest_name || 'Sense nom',
    channel: dto.canal || 'Direct',
    checkIn: dto.data_entrada,
    checkOut: dto.data_sortida,
    unitId: dto.unit_id || dto.room_id || '',
    guestsRegistered: dto.guests?.length ?? 0,
    guestsExpected: dto.total_hostes ?? (dto.num_adults ?? 0) + (dto.num_nens ?? 0),
    numAdults: dto.num_adults ?? 0,
    numChildren: dto.num_nens ?? 0,
    status: DTO_STATUS_MAP[dto.estat ?? ''] ?? 'confirmed',
    numNights: dto.nits ?? null,
    arrivalTime: dto.hora_arribada ?? null,
    totalPrice: dto.preu_total ?? null,
    deposit: dto.deposit ?? null,
    balance: dto.balance ?? null,
    guestPhone: dto.guest_phone ?? null,
    beds24PropertyId: dto.beds24_property_id ?? null,
    guestCountry: dto.guest_country ?? null,
    guestCountry2: dto.guest_country2 ?? null,
  };
}

/**
 * Transforma Unit (API) → RoomInfo (Calendar V2)
 * 🆕 Sprint 30 — Inclou name (nom original Beds24) i sortOrder per ordenació alternativa
 */
function transformUnit(unit: Unit): RoomInfo {
  return {
    id: unit.id,
    name: unit.name, // 🆕 Nom original Beds24
    displayName: unit.displayName || unit.name,
    physicalLocation: unit.physicalLocation || unit.room?.physicalLocation,
    beds24RoomId: unit.beds24RoomId,
    beds24UnitId: unit.beds24UnitId,
    sortOrder: unit.sortOrder, // 🆕 Per ordenació física
  };
}

/**
 * Unitats actives d'una propietat → files de la graella, amb l'ordre de la configuració.
 * 🆕 Sprint 30. En mode «Totes» s'aplica dins de cada bloc.
 */
function unitsToRooms(units: readonly Unit[], roomSortMode: RoomSortMode): RoomInfo[] {
  const active = units.filter((u: Unit) => u.isActive);

  const sorted = [...active].sort((a: Unit, b: Unit) => {
    switch (roomSortMode) {
      case 'nom_original':
        // Ordre original Beds24: per nom (numèric o alfabètic)
        return (a.name || '').localeCompare(b.name || '', 'ca', { numeric: true });
      case 'display_name':
        // Ordre alfabètic per nom configurat (H101, H102…)
        return (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '', 'ca', {
          numeric: true,
        });
      case 'sort_order':
      default:
        // Ordre físic per planta (unit_config.sort_order)
        return compareUnitsBySortOrder(a, b);
    }
  });

  return sorted.map(transformUnit);
}

/**
 * `combine` de les queries de la graella (una per propietat). Fora del component perquè sigui la
 * mateixa funció a cada render: així TanStack Query no la torna a executar si res no ha canviat.
 */
function combineCalendarQueries<T>(results: UseQueryResult<T>[]) {
  return {
    data: results.map((r) => r.data),
    // Query fallida (després dels reintents de TanStack): el bloc ensenya «Error carregant».
    errors: results.map((r) => r.isError),
    refetch: results.map((r) => r.refetch),
    // Dades d'abans (`placeholderData`) mentre n'arriben unes de noves: «Actualitzant...».
    updating: results.some((r) => r.isPlaceholderData),
  };
}
const combineBookings = (results: UseQueryResult<Booking[]>[]) => combineCalendarQueries(results);
const combineUnits = (results: UseQueryResult<Unit[]>[]) => combineCalendarQueries(results);

// ============================================
// R10 (runbook 7) — LA FITXA I L'HISTORIAL
// ============================================
//
// La fitxa oberta des de la graella és una entrada pròpia de l'historial (push), perquè el
// gest «enrere» d'Android la tanqui en lloc de sortir del calendari. `/calendar-v2` i
// `/reserves/:ids` són rutes germanes amb el mateix element: React Router conserva la mateixa
// instància de la pàgina, i la setmana i la pestanya no es perden en anar i tornar.
// Decisions pures i exportades perquè el test (environment node, sense historial) les provi.

/** Obrir una fitxa des de la graella: push si no n'hi ha cap d'oberta; si n'hi ha, replace. */
export function decideSheetOpen(
  sheetOpen: boolean,
  pushedFromGrid: boolean,
): { mode: 'push' | 'replace'; pushedFromGrid: boolean } {
  if (sheetOpen) return { mode: 'replace', pushedFromGrid };
  return { mode: 'push', pushedFromGrid: true };
}

/**
 * Tancar la fitxa: si l'hem apilada nosaltres, `-1` (torna a l'entrada del calendari, sense
 * deixar-ne cap de més); si no (URL oberta directament), replace cap al calendari.
 */
export function decideSheetClose(pushedFromGrid: boolean): 'back' | 'replace' {
  return pushedFromGrid ? 'back' : 'replace';
}

/** Sense fitxa a l'URL (tancada amb la creu, per POP o per un redireccionament), ja no hi ha res apilat. */
export function pushedFromGridAfterLocation(pushedFromGrid: boolean, hasSheetInUrl: boolean): boolean {
  return hasSheetInUrl ? pushedFromGrid : false;
}

// ============================================
// PAGE COMPONENT
// ============================================

/** El tema del calendari (colors de canal, gruix, densitat) embolcalla tota la pàgina. */
export default function CalendarV2Page() {
  return (
    <CalendarThemeProvider>
      <CalendarV2PageContent />
    </CalendarThemeProvider>
  );
}

function CalendarV2PageContent() {
  // Densitat (amplada de columna) i alçada de les files: del tema, que les treu de la roda dentada
  // (F5, F7).
  const { cellWidth, rowHeight } = useCalendarTheme();
  const queryClient = useQueryClient();

  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // ============================================
  // 🆕 F1 — LA RESERVA DE L'URL
  // ============================================

  const navigate = useNavigate();
  const { ids: idsParam } = useParams<{ ids?: string }>();

  /** Reserves del grup de l'URL. La primera és la que s'obre a la fitxa. */
  const focusIds = useMemo(() => parseReservationIds(idsParam), [idsParam]);
  const primaryBeds24Id = focusIds[0] ?? null;

  // Enllaç mal format (un cuid, xifres de més…): avís i al calendari, sense endevinar res.
  useEffect(() => {
    if (idsParam && focusIds.length === 0) {
      toast.error('Enllaç de reserva no vàlid');
      navigate(CALENDAR_PATH, { replace: true });
    }
  }, [idsParam, focusIds, navigate]);

  // ============================================
  // NAVIGATION
  // ============================================

  const goToToday = useCallback(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentDate(d);
  }, []);

  const goToPrevWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  }, []);

  // Format date for display
  const formatDateRange = useCallback((start: Date, days: number) => {
    const end = new Date(start);
    end.setDate(end.getDate() + days - 1);
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('ca-ES', opts)} - ${end.toLocaleDateString('ca-ES', opts)}`;
  }, []);

  // ============================================
  // SETTINGS (localStorage)
  // ============================================

  // 🆕 Sprint 30 — Configuració centralitzada (showCancelled + roomSortMode)
  const { settings, setSettings } = useCalendarSettings();

  // Compatibilitat: llegir showCancelled des de settings
  const showCancelled = settings.showCancelled;
  const roomSortMode = settings.roomSortMode;

  // ============================================
  // DATA FETCHING
  // ============================================

  // Propietats actives, en l'ordre del calendari: la 18770 primer, després per nom (§2.3).
  const { data: properties = [], isLoading: loadingProperties } = useProperties();
  const activeProperties = useMemo(
    () => sortPropertiesForCalendar(properties.filter((p: Property) => p.actiu)),
    [properties],
  );
  const activePropertyIds = useMemo(
    () => activeProperties.map((p: Property) => p.beds24_property_id),
    [activeProperties],
  );

  // Vista: pestanyes (una propietat) o totes seguides. Es desa al navegador (D1).
  const [propertyView, setPropertyView] = useState<PropertyView>(readPropertyView);
  const isAllView = propertyView === 'all';
  const handlePropertyViewChange = (view: PropertyView) => {
    setPropertyView(view);
    savePropertyView(view);
  };

  // Pestanya triada (desada al navegador, amb `try/catch` via `storage`).
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(readSavedPropertyId);

  useEffect(() => {
    if (selectedPropertyId) saveSelectedPropertyId(selectedPropertyId);
  }, [selectedPropertyId]);

  // Si la desada no és d'una propietat activa, la primera de l'ordre. Mentre les propietats
  // carreguen, la desada tal qual: així les reserves ja es demanen en paral·lel.
  const currentPropertyId = loadingProperties
    ? selectedPropertyId
    : resolveSelectedPropertyId(selectedPropertyId, activePropertyIds);

  // 🆕 F1 — Les reserves de l'URL, pel número de Beds24 (el frontend no sap el cuid intern).
  // Són sis com a màxim (ho talla el parser), i la primera és la que obre la fitxa.
  const focusQueries = useQueries({
    queries: focusIds.map((beds24Id) => ({
      queryKey: ['reservation-by-beds24', beds24Id],
      queryFn: async (): Promise<ReservationDetailDto> => {
        const res = await api.get(`/reservations/by-beds24/${beds24Id}`);
        return res.data;
      },
      retry: false,
    })),
  });

  const focusReservation = focusQueries[0]?.data;
  const focusNotFound = focusQueries[0]?.isError ?? false;

  /**
   * Un grup de Beds24 o unes reserves que algú ha posat juntes en un enllaç?
   *
   * Beds24 marca el grup amb `masterId`: la reserva mestra el porta buit i les altres hi
   * apunten (a la BD no hi ha cap fila amb `master_id = beds24_id`), per això es normalitza a
   * `master_id ?? beds24_id`. `bookingGroup` també val si algun dia s'omple — avui no n'hi ha
   * cap. Mentre falti carregar-ne alguna, es fa servir el text prudent.
   */
  /** Germanes de la reserva oberta, quan l'URL en porta una de sola (les dona el backend). */
  const membershipGroupIds = focusReservation?.group_beds24_ids ?? [];

  const isBeds24Group = (() => {
    if (focusIds.length < 2) return false;
    const loaded = focusQueries
      .map((q) => q.data)
      .filter((r): r is ReservationDetailDto => !!r);
    if (loaded.length !== focusIds.length) return false;

    const bookingGroups = loaded.map((r) => r.booking_group ?? '');
    if (bookingGroups.every((g) => g !== '') && new Set(bookingGroups).size === 1) return true;

    const masters = loaded.map((r) => r.master_id ?? r.beds24_id ?? '');
    return masters.every((m) => m !== '') && new Set(masters).size === 1;
  })();

  useEffect(() => {
    if (focusNotFound && primaryBeds24Id) {
      toast.error(`No s'ha trobat la reserva #${primaryBeds24Id}`);
      navigate(CALENDAR_PATH, { replace: true });
    }
  }, [focusNotFound, primaryBeds24Id, navigate]);

  // Situar el calendari: setmana del check-in i pestanya de la propietat de la reserva.
  // Només un cop per reserva, perquè després es pugui navegar per setmanes amb la fitxa oberta.
  const positionedForRef = useRef<string | null>(null);
  // Si la fitxa s'ha obert clicant una cel·la, la graella ja és on ha de ser: moure-la faria
  // saltar sota el dit. Només se situa quan la reserva ve de fora (enllaç, recàrrega, grup).
  const openedFromGridRef = useRef(false);
  // R10 — La fitxa oberta té una entrada pròpia a l'historial, apilada des de la graella.
  // Es buida quan l'URL ja no porta fitxa: creu, «enrere» del sistema (POP) o redireccionament.
  // Canviar de germana (replace) la manté: el `-1` segueix tornant al calendari.
  const sheetPushedRef = useRef(false);
  useEffect(() => {
    sheetPushedRef.current = pushedFromGridAfterLocation(sheetPushedRef.current, !!idsParam);
    // Fitxa tancada abans que la reserva carregués: que el pròxim enllaç sí que situï la graella.
    if (!idsParam) openedFromGridRef.current = false;
  }, [idsParam]);
  useEffect(() => {
    if (!focusReservation?.beds24_id) return;
    if (positionedForRef.current === focusReservation.beds24_id) return;
    positionedForRef.current = focusReservation.beds24_id;

    if (openedFromGridRef.current) {
      openedFromGridRef.current = false;
      return;
    }

    // `parseISO` i no `new Date(...)`: el DTO pot portar `2026-09-20` sense hora, i el
    // constructor l'interpretaria en UTC — a l'estiu, la setmana anterior.
    setCurrentDate(startOfWeekMonday(parseISO(focusReservation.data_entrada)));
    if (focusReservation.beds24_property_id) {
      setSelectedPropertyId(focusReservation.beds24_property_id);
    }
  }, [focusReservation]);

  // Rang de dades: un mes enrere i tretze endavant, arrodonit al mes (la clau no canvia a cada
  // setmana). El passat és el que pesa a la resposta: el perquè, a `stableDateRange`.
  const { from, to } = useMemo(() => stableDateRange(currentDate), [currentDate]);

  // Les propietats que es carreguen: en pestanyes, la de la pestanya; en «Totes», totes en ordre.
  const loadedPropertyIds = useMemo(
    () => (isAllView ? activePropertyIds : currentPropertyId ? [currentPropertyId] : []),
    [isAllView, activePropertyIds, currentPropertyId],
  );

  // Una query per propietat, amb les mateixes claus que `useCalendarBookings`/`useCalendarUnits`:
  // el cache es comparteix entre les pestanyes i «Totes», i les invalidacions de sempre hi arriben.
  // P7: amb `queryClient`, en canviar de mes la graella es queda amb les reserves d'abans de la
  // mateixa propietat («Actualitzant…») en lloc de buidar-se (`useQueries` no en guarda cap).
  const bookingResults = useQueries({
    queries: loadedPropertyIds.map((id) =>
      calendarBookingsQueryOptions(from, to, id, queryClient),
    ),
    combine: combineBookings,
  });
  const unitResults = useQueries({
    queries: loadedPropertyIds.map((id) => calendarUnitsQueryOptions(id)),
    combine: combineUnits,
  });

  // ============================================
  // DATA TRANSFORMATION
  // ============================================

  // Generate 14 days from today
  const dates = useMemo(() => generateDates(currentDate, 14), [currentDate]);

  /** Un bloc per propietat carregada: files (amb l'ordre de la configuració) i estat de càrrega. */
  const propertyBlocks = useMemo(
    () =>
      loadedPropertyIds.map((propertyId, i) => {
        const units = unitResults.data[i];
        const bookings = bookingResults.data[i];
        const state = calendarBlockState({
          hasUnits: !!units,
          hasBookings: !!bookings,
          unitsError: unitResults.errors[i] ?? false,
          bookingsError: bookingResults.errors[i] ?? false,
        });
        const refetchUnits = unitResults.refetch[i];
        const refetchBookings = bookingResults.refetch[i];
        return {
          propertyId,
          name:
            activeProperties.find((p: Property) => p.beds24_property_id === propertyId)?.nom ??
            String(propertyId),
          units: units ?? [],
          bookings: bookings ?? [],
          rooms: units ? unitsToRooms(units, roomSortMode) : [],
          ready: state === 'ready',
          error: state === 'error',
          // Reintentar només el que ha fallat (o no ha arribat): el que ja hi és no es torna a baixar.
          retry: () => {
            if (!units) void refetchUnits?.();
            if (!bookings) void refetchBookings?.();
          },
        };
      }),
    [
      loadedPropertyIds,
      unitResults.data,
      unitResults.errors,
      unitResults.refetch,
      bookingResults.data,
      bookingResults.errors,
      bookingResults.refetch,
      activeProperties,
      roomSortMode,
    ],
  );

  // Pestanyes: les files de la propietat de la pestanya.
  const rooms: RoomInfo[] = useMemo(
    () => (isAllView ? [] : (propertyBlocks[0]?.rooms ?? [])),
    [isAllView, propertyBlocks],
  );

  // «Totes»: un bloc de la graella per propietat, amb el nom a la fila separadora.
  const sections: CalendarGridSection[] | undefined = useMemo(
    () =>
      isAllView
        ? propertyBlocks.map((block) => ({
            id: String(block.propertyId),
            label: block.name,
            rooms: block.rooms,
            loading: !block.ready && !block.error,
            error: block.error,
            onRetry: block.retry,
          }))
        : undefined,
    [isAllView, propertyBlocks],
  );

  // 🛏️ Sprint 31 — Mapa unitId → Unit per al fallback de defaultBedConfig (totes les carregades).
  // I unitId → propietat: crear una reserva des d'una cel·la fa servir la propietat del bloc.
  const { unitMap, unitPropertyOf } = useMemo(() => {
    const map = new Map<string, Unit>();
    const propertyOf = new Map<string, number>();
    for (const block of propertyBlocks) {
      for (const u of block.units) {
        map.set(u.id, u);
        propertyOf.set(u.id, block.propertyId);
      }
    }
    return { unitMap: map, unitPropertyOf: propertyOf };
  }, [propertyBlocks]);

  // Transform bookings → reservations (filtrant cancel·lades segons configuració). Els grups de
  // Beds24 es compten sobre tot el que s'ha carregat (a «Totes», totes les propietats).
  const allReservations: ReservationCellData[] = useMemo(
    () =>
      bookingsToReservations(
        propertyBlocks.flatMap((block) => block.bookings),
        unitMap,
        showCancelled,
      ),
    [propertyBlocks, showCancelled, unitMap],
  );

  // 🛏️ Runbook 10 — Filtre «Llits per revisar» (automàtiques + avisos). Estat de la sessió.
  // P16: ni el comptador ni el filtre compten les cancel·lades, encara que es mostrin.
  const [bedsToReviewOnly, setBedsToReviewOnly] = useState(false);
  const bedsToReviewCount = useMemo(() => countBedsToReview(allReservations), [allReservations]);
  const reservations: ReservationCellData[] = useMemo(
    () => (bedsToReviewOnly ? allReservations.filter(isLiveBedToReview) : allReservations),
    [allReservations, bedsToReviewOnly],
  );

  // ============================================
  // HANDLERS
  // ============================================

  // 🆕 F1 — La fitxa oberta surt de l'URL, no d'un `useState`: així sobreviu a la recàrrega
  // i a l'enviament de l'enllaç. Primer es busca a la graella carregada; si la reserva no hi
  // és (fora de rang o d'una altra propietat), es fa servir la cel·la mínima del DTO.
  const selectedReservation: ReservationCellData | null = useMemo(() => {
    if (!primaryBeds24Id) return null;
    const fromGrid = allReservations.find((r) => r.beds24Id === primaryBeds24Id);
    if (fromGrid) return fromGrid;
    return focusReservation ? reservationDtoToCellData(focusReservation) : null;
  }, [primaryBeds24Id, allReservations, focusReservation]);

  const handleReservationClick = (id: string) => {
    const reservation = allReservations.find((r) => r.id === id);
    if (!reservation) return;
    if (!reservation.beds24Id) {
      toast.error('Aquesta reserva no té número de Beds24 i no es pot enllaçar');
      return;
    }
    openedFromGridRef.current = true;
    // R10 — push si no hi ha cap fitxa oberta (l'«enrere» del sistema la tancarà); si n'hi ha
    // una, replace, per no apilar una entrada per cada fitxa.
    const decision = decideSheetOpen(!!primaryBeds24Id, sheetPushedRef.current);
    sheetPushedRef.current = decision.pushedFromGrid;
    navigate(buildReservationPath([reservation.beds24Id]), {
      replace: decision.mode === 'replace',
    });
  };

  const handleCloseSheet = () => {
    if (decideSheetClose(sheetPushedRef.current) === 'back') {
      sheetPushedRef.current = false;
      navigate(-1);
      return;
    }
    navigate(CALENDAR_PATH, { replace: true });
  };

  // Get the ID for highlighting in the grid
  const selectedId = selectedReservation?.id || null;

  /**
   * Senyals de la card (§6 F2.5): amb una reserva d'un grup oberta, les del grup es ressalten a la
   * graella i la resta s'apaguen. `membershipGroupIds` són números de Beds24; la graella vol ids.
   */
  const groupBeds24Ids = focusReservation?.group_beds24_ids;
  const highlightIds = useMemo(() => {
    if (!groupBeds24Ids || groupBeds24Ids.length < 2) return undefined;
    const wanted = new Set(groupBeds24Ids);
    const ids = allReservations
      .filter((r) => r.beds24Id && wanted.has(r.beds24Id))
      .map((r) => r.id);
    return ids.length > 0 ? new Set(ids) : undefined;
  }, [groupBeds24Ids, allReservations]);

  const handlePropertyChange = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
  };

  // 🆕 R3 (runbook 6) — Tap a una cel·la buida → formulari de creació. Només ADMIN; és estat
  // local (no va a l'URL) i, amb una fitxa oberta, el tap no fa res.
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  // «Posar al dia»: qui pot marcar entrada, sortida i neteja (les mateixes rutes: ADMIN i STAFF).
  const canMarkStays = isAdmin || user?.role === 'STAFF';
  const [createTarget, setCreateTarget] = useState<CreateReservationTarget | null>(null);

  const handleDayClick = (date: Date, room: RoomInfo) => {
    // La propietat de la fila (en «Totes», la del bloc), no la de la pestanya.
    const propertyId = unitPropertyOf.get(room.id);
    if (!isAdmin || selectedId || !propertyId) return;
    setCreateTarget({
      unitId: room.id,
      unitName: room.displayName,
      date: format(date, 'yyyy-MM-dd'),
      propertyId,
      propertyName: activeProperties.find((p: Property) => p.beds24_property_id === propertyId)
        ?.nom,
    });
  };

  const handleReservationCreated = (created: CreatedReservation) => {
    setCreateTarget(null);
    // Una reserva sense número de Beds24 (`manual-…`) no té URL: es queda al calendari.
    const path = buildReservationPath([created.beds24_id]);
    if (path === CALENDAR_PATH) return;
    // La graella ja és a la setmana de la reserva nova: que no salti.
    openedFromGridRef.current = true;
    navigate(path, { replace: true });
  };

  // ============================================
  // RENDER
  // ============================================

  // «Carregant» dins de la graella, mai substituint la pàgina (§2.4): la capçalera, les pestanyes
  // i la navegació es queden mentre arriben les dades d'una pestanya o d'un mes nou.
  const gridUpdating = bookingResults.updating || unitResults.updating;
  const noActiveProperties = !loadingProperties && activeProperties.length === 0;
  const tabReady = propertyBlocks[0]?.ready ?? false;
  const tabError = propertyBlocks[0]?.error ?? false;

  const gridProps = {
    dates,
    reservations,
    selectedReservationId: selectedId,
    highlightIds,
    onReservationClick: handleReservationClick,
    onDayClick: isAdmin ? handleDayClick : undefined,
    // F3: moure i allargar amb el ratolí (configuració); el PATCH és només ADMIN.
    dragMode: isAdmin ? settings.dragMode : ('off' as const),
    cellWidth,
    cellHeight: rowHeight,
    className: 'flex-1',
  };

  let gridContent: React.ReactNode;
  if (noActiveProperties) {
    gridContent = (
      <div className="text-center py-8 text-gray-500">No hi ha cap propietat activa</div>
    );
  } else if (!isAllView && tabError) {
    gridContent = <GridError onRetry={() => propertyBlocks[0]?.retry()} />;
  } else if (isAllView ? loadingProperties : !tabReady) {
    gridContent = <GridLoading />;
  } else if (isAllView) {
    gridContent = <CalendarGrid {...gridProps} sections={sections} />;
  } else if (rooms.length > 0) {
    gridContent = <CalendarGrid {...gridProps} rooms={rooms} />;
  } else {
    gridContent = (
      <div className="text-center py-8 text-gray-500">
        No hi ha habitacions configurades per aquesta propietat
      </div>
    );
  }

  return (
    // P12: `h-full`, no `h-screen`. El layout (`isFullHeightRoute`) dona a la pàgina l'alçada que
    // queda sota la seva capçalera i no fa scroll: l'únic element amb scroll és la graella, i la
    // barra de dalt (‹ › Avui, data, roda dentada) queda sempre a la vista.
    <div className="h-full min-h-0 flex flex-col bg-gray-100 overflow-hidden">
      {/* Header: una sola fila, plegable (F7). Sense títol: ja és a la ruta de dalt. */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0 z-40">
        <CalendarToolbar
          rangeLabel={formatDateRange(currentDate, 14)}
          updating={gridUpdating}
          collapsed={settings.headerCollapsed}
          onCollapsedChange={(headerCollapsed) =>
            setSettings((prev) => ({ ...prev, headerCollapsed }))
          }
          leading={
            // Vista: una propietat per pestanya o totes seguides (es desa al navegador)
            <ToggleGroup
              type="single"
              size="sm"
              value={propertyView}
              onValueChange={(value) => {
                // Radix buida el valor si es torna a prémer l'actiu: no es deixa sense vista.
                if (value === 'tabs' || value === 'all') handlePropertyViewChange(value);
              }}
              aria-label="Vista de les propietats"
              className="flex-shrink-0 gap-0 rounded-full bg-gray-100 p-0.5"
            >
              <ToggleGroupItem
                value="tabs"
                className="h-7 rounded-full px-3 text-xs data-[state=on]:bg-white data-[state=on]:shadow-sm"
              >
                Pestanyes
              </ToggleGroupItem>
              <ToggleGroupItem
                value="all"
                className="h-7 rounded-full px-3 text-xs data-[state=on]:bg-white data-[state=on]:shadow-sm"
              >
                Totes
              </ToggleGroupItem>
            </ToggleGroup>
          }
          trailing={
            <>
              {/* «Posar al dia»: entrades, sortides i neteges de l'última setmana, de cop */}
              {canMarkStays && <StayCatchUp />}

              {/* 🛏️ Runbook 10 — Filtre «Llits per revisar» */}
              <button
                onClick={() => setBedsToReviewOnly((prev) => !prev)}
                aria-pressed={bedsToReviewOnly}
                className={cn(
                  'mr-1 h-7 px-3 text-xs font-medium rounded-full whitespace-nowrap border',
                  'transition-colors touch-manipulation flex-shrink-0',
                  bedsToReviewOnly
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50',
                )}
              >
                Llits per revisar ({bedsToReviewCount})
              </button>

              <button
                onClick={goToPrevWeek}
                className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                aria-label="Setmana anterior"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>

              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 transition-colors touch-manipulation"
              >
                Avui
              </button>

              <button
                onClick={goToNextWeek}
                className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                aria-label="Setmana següent"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>

              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="p-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                    aria-label="Seleccionar data"
                  >
                    <CalendarDays className="w-5 h-5 text-gray-600" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={currentDate}
                    onSelect={(date) => date && setCurrentDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Senyals de la card (§6 F2.4): llegenda de la franja, els llums i les marques */}
              <CalendarLegend />

              {/* 🆕 Sprint 30 — Settings Dialog (showCancelled + roomSortMode) */}
              <CalendarSettingsDialog settings={settings} onSettingsChange={setSettings} />
            </>
          }
          below={
            // Pestanyes de propietat: fila baixa pròpia (5 propietats amb noms llargs no caben a la
            // fila de dalt sense amagar-ne cap).
            !isAllView && activeProperties.length > 0 ? (
              <div className="flex items-center gap-1.5 px-4 pb-1.5 overflow-x-auto">
                {activeProperties.map((property: Property) => (
                  <TabButton
                    key={property.id}
                    active={currentPropertyId === property.beds24_property_id}
                    onClick={() => handlePropertyChange(property.beds24_property_id)}
                  >
                    {property.nom}
                  </TabButton>
                ))}
              </div>
            ) : null
          }
        />

        {/*
          🆕 F1 — Grup de reserves de l'URL. Enllaços, no pestanyes dins de la fitxa: les
          pestanyes obligarien a refer la `ReservationSheet` (925 línies, bug #12 obert) i el
          pla les deixa per a F4. Clicar-ne una canvia quina fitxa es veu i manté el grup a l'URL.
        */}
        {focusIds.length > 1 && (
          // `data-sheet-outside-safe`: la fitxa és `modal={false}` i el seu
          // `onInteractOutside` la tancaria al `pointerdown`, desmuntant aquesta barra abans
          // que el `click` arribés al xip — i el clic acabava a `/calendar-v2`.
          <div
            data-sheet-outside-safe
            className="flex items-center gap-2 px-4 pb-2 overflow-x-auto"
          >
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {isBeds24Group ? 'Grup de Beds24' : 'Reserves enllaçades'}: {focusIds.length}
            </span>
            {focusIds.map((id) => (
              <button
                key={id}
                onClick={() => navigate(buildGroupPathFocusedOn(id, focusIds), { replace: true })}
                className={`px-2 py-1 text-xs font-mono rounded-full whitespace-nowrap transition-colors ${
                  id === primaryBeds24Id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                #{id}
              </button>
            ))}
          </div>
        )}

        {/*
          🆕 F1 — Una sola reserva de l'URL que forma part d'un grup de Beds24. Sense això, la
          fitxa no diu enlloc que hi ha germanes i la reserva sembla solitària.
        */}
        {focusIds.length === 1 && membershipGroupIds.length > 1 && (
          <div
            data-sheet-outside-safe
            className="flex items-center gap-2 px-4 pb-2 overflow-x-auto"
          >
            <span className="text-xs text-gray-500 whitespace-nowrap">
              Forma part d'un grup de Beds24:
            </span>
            {membershipGroupIds.map((id) => (
              <button
                key={id}
                onClick={() => navigate(buildReservationPath([id]), { replace: true })}
                className={`px-2 py-1 text-xs font-mono rounded-full whitespace-nowrap transition-colors ${
                  id === primaryBeds24Id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                #{id}
              </button>
            ))}
            <button
              onClick={() => navigate(buildReservationPath(membershipGroupIds), { replace: true })}
              className="text-xs text-blue-600 hover:underline whitespace-nowrap"
            >
              Obre el grup
            </button>
          </div>
        )}
      </header>

      {/* Calendar Grid */}
      <main className="flex-1 min-h-0 overflow-hidden p-2 sm:p-4 flex flex-col">
        {gridContent}
      </main>

      {/* Reservation Sheet */}
      <ReservationSheet
        reservation={selectedReservation}
        open={!!selectedId}
        onClose={handleCloseSheet}
      />

      {/* 🆕 R3 — Crear reserva des d'una cel·la buida */}
      {isAdmin && (
        <CreateReservationSheet
          target={createTarget}
          onClose={() => setCreateTarget(null)}
          onCreated={handleReservationCreated}
        />
      )}
    </div>
  );
}

// ============================================
// GRID LOADING
// ============================================

/** El «Carregant» de la graella: dins del `<main>`, amb la capçalera al seu lloc. */
function GridLoading() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="text-sm text-gray-500">Carregant calendari...</p>
      </div>
    </div>
  );
}

/**
 * Una query de la graella ha fallat (en pestanyes): «Error carregant» amb un botó per tornar-ho a
 * provar, dins del `<main>`, en lloc d'un «Carregant» que no s'acabaria mai.
 */
export function GridError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-1 items-center justify-center" role="alert">
      <div className="flex flex-col items-center gap-2">
        <AlertTriangle className="h-8 w-8 text-red-500" aria-hidden="true" />
        <p className="text-sm text-gray-700">Error carregant el calendari</p>
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 touch-manipulation"
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
          Reintentar
        </button>
      </div>
    </div>
  );
}

// ============================================
// TAB BUTTON
// ============================================

interface TabButtonProps {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}

function TabButton({ children, active = false, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        h-7 px-3 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0
        transition-colors touch-manipulation
        ${active ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
      `}
    >
      {children}
    </button>
  );
}
